// Oliver Sweeney — Search matching engine
// Reads assets/search-vocabulary.json (colour / category / merchandising /
// informational dictionaries — built and pushed by the portal's
// searchvocabularygenerate job from search_overrides + search_category_
// collections + live product data) and resolves a search query to a
// redirect destination, or null if nothing resolves.
//
// Anchor rule: colour never resolves on its own (there's no single "black"
// destination) - it only combines with a category. A category on its own,
// or an exact merchandising/informational phrase, can resolve alone.
//
// Product-name matching is deliberately out of scope here - native Shopify
// predictive search already covers that well (see PredictiveSearch below
// in os-global.js); this engine only covers what that doesn't.
class SearchEngine {
  constructor() {
    this._vocab = null
    this._loading = null
  }

  // Lazy-loads the vocabulary on first use, not on page load, so it never
  // competes with the page's own initial render.
  _load() {
    if (this._vocab) return Promise.resolve(this._vocab)
    if (this._loading) return this._loading
    this._loading = fetch(window.OS_SEARCH_VOCAB_URL)
      .then(r => r.json())
      .catch(() => ({}))
      .then(vocab => {
        this._vocab = Object.assign({ colours: {}, categories: {}, merchandising: {}, informational: {}, suggestions: [] }, vocab)
        return this._vocab
      })
    return this._loading
  }

  // Submit-time resolution: exact matches only. Returns { url, method,
  // label } or null.
  async resolve(query) {
    const vocab = await this._load()
    const q = this._normalize(query)
    if (!q) return null
    return this._match(q.split(' '), vocab)
  }

  // Live-typing suggestions: an array (possibly empty) combining
  //  1. the same exact deterministic match resolve() would find (so a
  //     fully-typed query still resolves before submit, e.g. pasted text);
  //     with
  //  2. human-approved compound suggestions from search_suggestions,
  //     prefix-matched against whatever's been typed so far - e.g. typing
  //     "bla" or "shoes" both surfacing "Black Shoes" if that's been
  //     reviewed and approved in Search Management.
  // Deliberately NOT prefix-matched against the raw colour/category
  // dictionaries themselves - with a short/ambiguous prefix that just
  // returns whichever entry happens to sort first, which looks like a
  // confident answer but isn't one. Early/partial-word suggestions only
  // ever come from the approved list, never guessed live.
  async suggest(query) {
    const vocab = await this._load()
    const q = this._normalize(query)
    if (!q) return []
    const tokens = q.split(' ')

    const results = []
    const exact = this._match(tokens, vocab)
    if (exact) results.push(exact)

    for (const s of this._matchSuggestions(tokens, vocab.suggestions)) {
      if (!results.some(r => r.url === s.url)) results.push(s)
      if (results.length >= 5) break
    }
    return results
  }

  // A suggestion matches when every typed word (complete or partial) is a
  // prefix of some word in its trigger phrase, in any order - so "bla"
  // and "shoes" both match a "black shoes" suggestion, same as the
  // colour/category matching below treats word order as irrelevant.
  _matchSuggestions(tokens, suggestions) {
    return suggestions
      .filter(s => {
        const triggerWords = s.trigger.split(' ')
        return tokens.every(t => triggerWords.some(tw => tw.startsWith(t)))
      })
      .map(s => ({ url: s.url, method: 'suggested', label: s.label }))
  }

  _match(tokens, vocab) {
    const q = tokens.join(' ')

    // exact-phrase matches first - highest confidence, and the only way to
    // catch multi-word terms like "return policy" or "check shirt"
    if (vocab.merchandising[q] !== undefined) return { url: vocab.merchandising[q], method: 'merchandising', label: this._titleCase(q) }
    if (vocab.informational[q] !== undefined) return { url: vocab.informational[q], method: 'informational', label: this._titleCase(q) }
    if (vocab.categories[q] !== undefined) return { url: this._categoryUrl(vocab.categories[q]), method: 'category', label: vocab.categories[q].value }

    // token-level: the longest category phrase anywhere in the query,
    // combined with the longest colour phrase anywhere else in it. Order
    // of the words doesn't matter ("black shoes" / "shoes black" both
    // resolve), and unrecognised words (e.g. "men's", "please") are
    // ignored rather than blocking the match.
    const category = this._findPhrase(tokens, vocab.categories)
    if (!category) return null

    const colour = this._findPhrase(tokens, vocab.colours)
    return {
      url: this._categoryUrl(category.value, colour ? colour.value : undefined),
      method: colour ? 'colour_category' : 'category',
      label: colour ? `${colour.value} ${category.value.value}` : category.value.value,
    }
  }

  _titleCase(s) {
    return s.replace(/\b\w/g, c => c.toUpperCase())
  }

  _normalize(query) {
    return (query || '').toLowerCase().trim().replace(/\s+/g, ' ')
  }

  // Scans all contiguous word spans, longest first, for an exact match in
  // `dict` - so "check shirt" wins over a bare "shirt", "flip flops" wins
  // over "flip", etc.
  _findPhrase(tokens, dict) {
    for (let len = tokens.length; len >= 1; len--) {
      for (let start = 0; start + len <= tokens.length; start++) {
        const phrase = tokens.slice(start, start + len).join(' ')
        if (dict[phrase] !== undefined) return { term: phrase, value: dict[phrase] }
      }
    }
    return null
  }

  // shopify.color-pattern is a metaobject-reference field - the native
  // filter needs the swatch's metaobject GID, not its label. We're the
  // ones initiating this navigation, so we resolve it directly rather than
  // landing on a friendly param and swapping it client-side after the
  // fact - same final URL either way, one less redirect hop.
  // GIDs are Shopify's standard shopify--color-pattern metaobjects (the
  // 15-swatch taxonomy) - effectively static, safe to hardcode.
  static COLOUR_GIDS = {
    black: '310931947894', brown: '99783180480', clear: '415221907830',
    blue: '470979969398', white: '553506537846', beige: '553506701686',
    tan: '553506832758', yellow: '553506898294', navy: '553506931062',
    grey: '553506996598', red: '553507160438', green: '553507193206',
    pink: '553507225974', purple: '553507258742', orange: '553507291510',
  }

  _categoryUrl(category, colourValue) {
    let url = `/collections/${category.collection}`
    const gid = colourValue && SearchEngine.COLOUR_GIDS[colourValue.toLowerCase()]
    if (gid) url += `?filter.v.t.shopify.color-pattern=${encodeURIComponent('gid://shopify/Metaobject/' + gid)}`
    return url
  }
}

window.OSSearchEngine = new SearchEngine()
